import {
  VegaButton,
  VegaButtonCircle,
  VegaCard,
  VegaChip,
  VegaDivider,
  VegaFlex,
  VegaFont,
  VegaGrid,
  VegaPopover,
} from '@globalpayments/vega-react';
import { groupBy } from 'lodash';

import { QueueModel, QueueStatusCheckModel } from '../../../types/queueStatus';
import styles from '../index.module.css';

interface QueueStatusApiGroupProps {
  apiName: string;
  apiQueues: QueueStatusCheckModel[];
  pendingActionKey?: string;
  onAction: (queueModel: QueueModel, actionName: string) => void;
}

const isDeadLetterQueue = (subQueueName: string) => /dead[- ]?letter/i.test(subQueueName);

/** Renders the grouped queues and actions for a single API. */
const QueueStatusApiGroup = ({ apiName, apiQueues, pendingActionKey, onAction }: QueueStatusApiGroupProps) => {
  const queuesByName = groupBy(apiQueues, (status) => status.queueModel.queueName);
  const queueGroups = Object.entries(queuesByName);
  const apiMessageCount = apiQueues.reduce((total, status) => total + status.count, 0);

  return (
    <VegaCard key={apiName} padding="size-16" variant="border">
      <VegaFlex direction="col" gap="size-16">
        <VegaFlex alignItems="center" flexWrap="wrap" gap="size-12" justifyContent="space-between">
          <VegaFont as="h3" color="text-secondary" variant="font-h5">
            {apiName}
          </VegaFont>
          <VegaFlex flexWrap="wrap" gap="size-8">
            <VegaChip
              chipType="label"
              size="extra-small"
              text={`${queueGroups.length} ${queueGroups.length === 1 ? 'queue' : 'queues'}`}
              variant="outlined"
            />
            <VegaChip
              chipType="label"
              size="extra-small"
              text={`${apiMessageCount} ${apiMessageCount === 1 ? 'message' : 'messages'}`}
              variant="outlined"
            />
          </VegaFlex>
        </VegaFlex>
        <VegaGrid className={styles.queueHeader} column={3} gap="size-16">
          <VegaFont as="span" color="text-secondary" variant="font-field-label-em">
            Queue
          </VegaFont>
          <VegaFont as="span" color="text-secondary" variant="font-field-label-em">
            Messages
          </VegaFont>
          <VegaFont as="span" color="text-secondary" variant="font-field-label-em">
            Actions
          </VegaFont>
        </VegaGrid>
        <VegaDivider />
        <VegaFlex direction="col" gap="size-8">
          {queueGroups.map(([queueName, queueStatuses]) => (
            <QueueStatusRow
              key={`${apiName}-${queueName}`}
              pendingActionKey={pendingActionKey}
              queueName={queueName}
              queueStatuses={queueStatuses}
              onAction={onAction}
            />
          ))}
        </VegaFlex>
      </VegaFlex>
    </VegaCard>
  );
};

interface QueueStatusRowProps {
  queueName: string;
  queueStatuses: QueueStatusCheckModel[];
  pendingActionKey?: string;
  onAction: (queueModel: QueueModel, actionName: string) => void;
}

const QueueStatusRow = ({ queueName, queueStatuses, pendingActionKey, onAction }: QueueStatusRowProps) => (
  <VegaFlex direction="col" gap="size-8">
    <VegaGrid className={styles.queueRow} column={{ default: 1, L: 3 }} gap="size-16">
      <VegaFlex direction="col" gap="size-4">
        <VegaFont as="span" className={styles.mobileLabel} color="text-secondary" variant="font-field-label-em">
          Queue
        </VegaFont>
        <VegaFont as="span" className={styles.queueName} variant="font-p2-short">
          {queueName}
        </VegaFont>
      </VegaFlex>
      <VegaFlex direction="col" gap="size-4">
        <VegaFont as="span" className={styles.mobileLabel} color="text-secondary" variant="font-field-label-em">
          Messages
        </VegaFont>
        <VegaFlex flexWrap="wrap" gap="size-8">
          {queueStatuses.map((queueStatus) => {
            const subQueueName = queueStatus.queueModel.subQueueName;
            const isDeadLetter = isDeadLetterQueue(subQueueName);
            return (
              <VegaChip
                aria-label={subQueueName}
                chipType="status"
                className={isDeadLetter && queueStatus.count === 0 ? styles.emptyDeadLetterChip : undefined}
                key={subQueueName}
                size="extra-small"
                statusColor={isDeadLetter ? 'bg-status-warning' : 'bg-status-success'}
                text={`${isDeadLetter ? 'Dead letter' : 'Active'} ${queueStatus.count}`}
                variant="outlined"
              />
            );
          })}
        </VegaFlex>
      </VegaFlex>
      <VegaFlex alignItems="center" className={styles.queueActions} gap="size-8">
        {queueStatuses.some((queueStatus) => queueStatus.availableQueueActionNames.length > 0) ? (
          queueStatuses.map((queueStatus) => (
            <QueueStatusActions
              key={queueStatus.queueModel.subQueueName}
              pendingActionKey={pendingActionKey}
              queueName={queueName}
              queueStatus={queueStatus}
              onAction={onAction}
            />
          ))
        ) : (
          <VegaFont as="span" color="text-secondary" variant="font-p2-short">
            No actions
          </VegaFont>
        )}
      </VegaFlex>
    </VegaGrid>
    <VegaDivider />
  </VegaFlex>
);

interface QueueStatusActionsProps {
  queueName: string;
  queueStatus: QueueStatusCheckModel;
  pendingActionKey?: string;
  onAction: (queueModel: QueueModel, actionName: string) => void;
}

const QueueStatusActions = ({ queueName, queueStatus, pendingActionKey, onAction }: QueueStatusActionsProps) => {
  const queueModel = queueStatus.queueModel;
  if (queueStatus.availableQueueActionNames.length === 0) return null;

  return (
    <VegaPopover alignment="end" placement="bottom" trigger="click">
      <VegaButtonCircle
        aria-label={`Actions for ${queueName} ${queueModel.subQueueName}`}
        icon="fa-solid fa-ellipsis"
        label={`Actions for ${queueName} ${queueModel.subQueueName}`}
        size="small"
        slot="popover-content"
      />
      <VegaFlex direction="col" gap="size-4" slot="popover">
        {queueStatus.availableQueueActionNames.map((actionName) => {
          const actionKey = `${queueModel.apiName}-${queueModel.queueName}-${queueModel.subQueueName}-${actionName}`;
          return (
            <VegaButton
              data-testid={actionName}
              key={actionName}
              label={actionName}
              loading={pendingActionKey === actionKey}
              onVegaClick={() => onAction(queueModel, actionName)}
              size="small"
              variant="secondary"
            />
          );
        })}
      </VegaFlex>
    </VegaPopover>
  );
};

export default QueueStatusApiGroup;
